var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import clsx from 'clsx';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { isNil, useClasses, useConsoleLog, useLayoutProps, useProps } from '@hawkins/helpers';
import { DatePicker } from '../datePicker';
import { Divider } from '../divider';
import { TimePicker } from '../timePicker';
import { useDateTimePickerStyles } from './dateTimePicker.styles';
dayjs.extend(utc);
var displayName = 'DateTimePicker';
/**
 * `<DateTimePicker>` allows the user to select a specific date and time.
 *
 * `<DateTimePicker>` consists of the picker itself. `<DateTimePickerDropdown>` and `<DateTimePickerInput>`
 * can be used to render the picker in the context of a dropdown toggle or input field.
 *
 * If the control you are looking for is not represented by any of these components,
 * you can use `<Popover>` to create a custom date-time picker according to your needs.
 */
export var DateTimePicker = React.forwardRef(function (props, ref) {
    // Props
    var _a = useProps(displayName, props), classesProp = _a.classes, className = _a.className, _b = _a.component, Component = _b === void 0 ? 'div' : _b, _c = _a.datePickerProps, _d = _c === void 0 ? {} : _c, _e = _d.classes, datePickerClassesProp = _e === void 0 ? undefined : _e, datePickerProps = __rest(_d, ["classes"]), footer = _a.footer, layout = _a.layout, onSelectedChange = _a.onSelectedChange, selected = _a.selected, startingDate = _a.startingDate, styleProp = _a.style, timePickerProps = _a.timePickerProps, _f = _a.use12HourClock, use12HourClock = _f === void 0 ? false : _f, _g = _a.withSeconds, withSeconds = _g === void 0 ? false : _g, restProps = __rest(_a, ["classes", "className", "component", "datePickerProps", "footer", "layout", "onSelectedChange", "selected", "startingDate", "style", "timePickerProps", "use12HourClock", "withSeconds"]);
    // hooks
    useConsoleLog(true, '⚠️Deprecation warning⚠️\nThis component has been deprecated, in favor of using the <DatePicker /> and its new variant variant="datetimepicker". For more information, checkout the examples.\n', { displayName: displayName, docsPath: 'components-date-picker#datetimepicker-variant-usage' });
    // Refs
    var prevSelectedRef = React.useRef(selected);
    prevSelectedRef.current = selected;
    var onDatePickerChange = React.useCallback(function (arg) {
        var prevSelected = prevSelectedRef.current;
        // Get the newly selected date-time and convert it to the local time zone
        // (the date options in <DatePicker> represent dates in the local time zone
        // even though the component uses UTC ISO strings).
        //
        // Then, take the previously selected date-time (also converted to the local time zone)
        // and copy the newly selected local month/date/year into it.
        //
        // This way, we retain the selected time
        // (rather than resetting to midnight every time a date is selected)
        // and also ensure that the <DatePicker> displays the correct local date.
        var rawNewSelectedDayLocal = dayjs.utc(Array.isArray(arg) ? arg[0] : arg).local();
        var modifiedNewSelectedDayLocal = dayjs
            .utc(prevSelected)
            .local()
            .set('month', rawNewSelectedDayLocal.get('month'))
            .set('date', rawNewSelectedDayLocal.get('date'))
            .set('year', rawNewSelectedDayLocal.get('year'));
        // If there was a previously selected date-time, retain the previous time.
        // Otherwise, set the time to the start of the day.
        var newSelectedDate = (prevSelected ? modifiedNewSelectedDayLocal : modifiedNewSelectedDayLocal.startOf('day')).toISOString();
        onSelectedChange === null || onSelectedChange === void 0 ? void 0 : onSelectedChange(newSelectedDate);
    }, [onSelectedChange]);
    var onTimePickerChange = React.useCallback(function (arg) {
        onSelectedChange === null || onSelectedChange === void 0 ? void 0 : onSelectedChange(arg === null || arg === void 0 ? void 0 : arg.toISOString());
    }, [onSelectedChange]);
    // Styles
    var baseClasses = useDateTimePickerStyles();
    var classes = useClasses(displayName, baseClasses, classesProp);
    var style = useLayoutProps({ layout: layout, style: styleProp });
    var datePickerCalendarWrapperClassName = classes.datePickerCalendarWrapper;
    var rootClassName = clsx(classes.HAWKINS_BASE, className, classes.root);
    var datePickerClasses = React.useMemo(function () { return (__assign({ calendarWrapper: datePickerCalendarWrapperClassName }, datePickerClassesProp)); }, [datePickerCalendarWrapperClassName, datePickerClassesProp]);
    return (_jsxs(Component, __assign({ className: rootClassName, ref: ref, style: style }, restProps, { children: [_jsxs("div", __assign({ className: classes.pickersContainer }, { children: [_jsx(DatePicker, __assign({ classes: datePickerClasses, onSelectedChange: onDatePickerChange, selected: selected, startingDate: startingDate }, datePickerProps)), _jsx(Divider, { className: classes.divider, color: "xLight", variant: "vertical" }), _jsx(TimePicker, __assign({ date: selected ? new Date(selected) : null, onChange: onTimePickerChange, use12HourClock: use12HourClock, withSeconds: withSeconds }, timePickerProps))] })), !isNil(footer) && _jsx("div", __assign({ className: classes.footer }, { children: footer }))] })));
});
DateTimePicker.displayName = displayName;
